import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { selectAppliedOffers } from '../../store/slices/OfferSlice';
import { selectGiftCard } from '../../store/slices/GiftCardSlice';
import { selectCachedOrder } from '../../store/slices/OrderSlice';
import { COUPON_TYPES } from '../../services/exports/Constants';
import { DiscountKind } from './useDiscountExclusivity';
import useBasket from '../../hooks/basket/useBasket';
import useAppMethods from '../../hooks/utility/useAppMethods';

/**
 * FOO-2555 / PROD-407 Inc 3 — discount-card availability/applied/replaces tag.
 *
 * A discount card (offer or promo, in the deals carousel) shows a status tag that reflects
 * the live exclusivity state, per the PM prototype:
 *   - `available` → "Available now"
 *   - `applied`   → "✓ Applied · €X off"
 *   - `replaces`  → "⚠️ Cannot be combined with [a promo code] · Replaces [CODE]" when applying
 *                    this card would displace another active discount.
 *
 * Model (plan rev 3): only ONE of {offer, promo code, gift card, points} applies per order —
 * applying one displaces the others. Multiple offers may still stack, so an offer card is
 * never blocked by another offer.
 *
 * This is a visibility-only derivation — it reads Redux and never mutates anything.
 */

export type DiscountCardStatus = 'available' | 'applied' | 'replaces';

export interface DiscountCardTag {
  status: DiscountCardStatus;
  /** `applied` — the savings string ("€4.00" / "20%"), when known. */
  savings?: string;
  /** `replaces` — the kind of discount that would be displaced (drives the type label). */
  activeKind?: DiscountKind;
  /** `replaces` — the specific identifier of that discount (promo code / offer name / gift mask). */
  activeName?: string;
  /** `replaces` with kind `offer` — how many offers are active (>1 → count/total copy, item 6). */
  activeCount?: number;
  /** `replaces` with kind `offer` and count>1 — pre-formatted € total of those offers. */
  activeTotal?: string;
}

export interface DiscountCardTagInput {
  /** The kind of discount this card represents. */
  kind: DiscountKind;
  /** Whether this card's discount is the one currently applied. */
  isApplied: boolean;
  /** Savings string for the applied tag ("€4.00" / "20%"). */
  savings?: string;
}

/**
 * Returns a pure resolver `(input) => DiscountCardTag`. The hook reads the exclusivity state
 * once; the resolver can then be called per-card inside a render loop (hooks can't be).
 */
export default function useDiscountCardTag(): (input: DiscountCardTagInput) => DiscountCardTag {
  const appliedOffers = useSelector(selectAppliedOffers);
  const giftCard = useSelector(selectGiftCard);
  const cachedOrder = useSelector(selectCachedOrder);
  const { i18n } = useTranslation();
  // Guarded so this visibility-only hook still works if rendered outside the basket/app providers.
  const previewOfferSaving = useBasket()?.previewOfferSaving ?? (() => 0);
  const formatCurrency = useAppMethods()?.formatCurrency ?? ((v: number) => String(v));

  const offerCount = appliedOffers?.length ?? 0;
  const hasOffer = offerCount > 0;
  const hasGift = Boolean(giftCard);
  const hasPromo = cachedOrder?.coupon_type === COUPON_TYPES.PROMO_CODE;
  const hasPoints = cachedOrder?.coupon_type === COUPON_TYPES.CASHBACK && (cachedOrder?.cashback_points ?? 0) > 0;
  const promoCode: string | undefined = cachedOrder?.coupon_details?.code;
  const giftMask: string | undefined = giftCard?.code ? `${giftCard.code.slice(0, 4)}…` : undefined;

  // The specific identifier of the currently-active discount of `kind`, for the replaces message.
  // For an offer we surface its (AI-generated) title when present; without one, the card falls
  // back to the generic "an offer" type label (activeName undefined) — no need to drag the full
  // offer-label machinery (and its provider deps) into this visibility-only hook.
  const nameOf = (kind: DiscountKind): string | undefined => {
    switch (kind) {
      case 'offer':
        return appliedOffers?.[0]?.translations?.[i18n.language]?.title || undefined;
      case 'promo':
        return promoCode;
      case 'gift':
        return giftMask;
      case 'points':
      default:
        return undefined; // points has no identifier to surface
    }
  };

  return ({ kind, isApplied, savings }: DiscountCardTagInput): DiscountCardTag => {
    if (isApplied) {
      return { status: 'applied', savings };
    }

    // The active discount (of a different kind) this card would displace. Offers stack (an offer
    // card is not blocked by another offer) and an offer coexists with points (an offer card is
    // not blocked by a points redemption) — CTO-confirmed 2026-08-03.
    const blockedByPoints = kind !== 'offer' && kind !== 'points' && hasPoints;
    const activeKind: DiscountKind | null =
      kind !== 'offer' && hasOffer
        ? 'offer'
        : kind !== 'promo' && hasPromo
          ? 'promo'
          : kind !== 'gift' && hasGift
            ? 'gift'
            : blockedByPoints
              ? 'points'
              : null;

    if (!activeKind) {
      return { status: 'available' };
    }

    // Several active offers → surface a count + total instead of a single name (item 6).
    if (activeKind === 'offer' && offerCount > 1) {
      const offersTotal = (appliedOffers ?? []).reduce((sum, offer) => sum + (previewOfferSaving(offer) || 0), 0);

      return {
        status: 'replaces',
        activeKind,
        activeCount: offerCount,
        activeTotal: String(formatCurrency(offersTotal)),
      };
    }

    return { status: 'replaces', activeKind, activeName: nameOf(activeKind) };
  };
}
